import UploadCompAnt from '@/components/UploadCompAnt';
import validateData from '@/helpers/validationHelpers/validationSchema';
import { CustomFormHelperText } from '@/module/all-screenings/component/ViewPatientRegistration/ModalInputServices';
import { TYPE_MODAL } from '@/module/telecounsel-appointments/constants';
import CustomDate from '@/new-components/CustomDate';
import CustomLoadingButton from '@/new-components/CustomLoadingButton';
import customToast from '@/new-components/CustomNotification';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  TextField,
  Typography,
  withStyles,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { get } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

const FILE_SIZE = 2 * 1024 * 1024; // ~2mb

const titleStyles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
    color: theme.palette.grey.subText,
  },
  text: {
    fontWeight: 600,
  },
});

const CustomDialogTitle = withStyles(titleStyles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <DialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6" className={classes.text}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
});

const CustomDialog = withStyles({
  paper: {
    borderRadius: 16,
    width: 480,
  },
})(Dialog);

const CustomDialogContent = withStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),

    overflowY: 'auto',
    maxHeight: '70vh',
  },
}))(DialogContent);

const CustomDialogActions = withStyles({
  root: {
    margin: 0,
    padding: 16,
    justifyContent: 'flex-end',
  },
})(DialogActions);

const CustomInputLabel = withStyles((theme) => ({
  root: {
    color: '#666666',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
}))(InputLabel);

const CustomTextField = withStyles({
  root: {
    fontSize: 20,
    width: '100%',

    '& .MuiOutlinedInput-root': {
      height: 48,
      borderRadius: 8,
    },
    '& .MuiOutlinedInput-input': {
      marginRight: 0,
      padding: '8px 5px',
      fontSize: 16,
    },
    '& .Mui-disabled': {
      '&.MuiOutlinedInput-root': {
        backgroundColor: '#f6f6f6',
      },
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      padding: 0,
    },
    '& .MuiFormHelperText-root': {
      alignItems: 'center',
      display: 'flex',
      fontFamily: 'OpenSans',
      fontSize: 12,
      gap: 6,
      marginLeft: 2,

      '& .MuiSvgIcon-root': {
        fontSize: 'unset',
      },
    },
  },
})(TextField);

const ModalAddOrEditBanner = ({
  typeModal,
  open,
  onClose,
  onSubmit,
  selectedItem,
  loading,
  totalItem,
}) => {
  const [errors, setErrors] = useState({});

  const [formValue, setFormValue] = useState({
    startDate: null,
    endDate: null,
    order: '',
    name: '',
    url: '',
  });

  useEffect(() => {
    if (typeModal === TYPE_MODAL.Edit) setFormValue(selectedItem);
  }, [typeModal, selectedItem]);

  const isMatchedPattern = (key, value) => {
    switch (key) {
      case 'order':
        const reg = /^\d+$/;
        return value.match(reg) || value === '';

      default:
        return true;
    }
  };

  const handleChangeForm = (key) => (e, newValue) => {
    let value;
    switch (key) {
      case 'url':
        if (get(e, 'size') > FILE_SIZE) {
          customToast('error', 'Image is too large');
          return;
        }
        value = e;
        break;
      case 'startDate':
        const newStartDate = moment(e).startOf('date').format();
        value = newStartDate;
        break;
      case 'endDate':
        const newEndDate = moment(e).endOf('date').format();
        value = newEndDate;
        break;
      default:
        value = get(e, 'target.value');
    }
    if (!isMatchedPattern(key, value)) return;

    setFormValue({ ...formValue, [key]: value });
  };

  const handleSubmit = async () => {
    try {
      await validateData(
        'bannerSchema',
        { ...formValue, totalItem: totalItem + 1 },
        (data) => {
          onSubmit({
            ...data,
            name: data.name.trim(),
          });
        }
      );
    } catch (errs) {
      setErrors(errs);
    }
  };

  return (
    <CustomDialog
      scroll="body"
      disableEnforceFocus
      open={open}
      onClose={onClose}
      disableBackdropClick
    >
      <CustomDialogTitle onClose={onClose}>
        {typeModal === TYPE_MODAL.Create
          ? 'Create new banner'
          : `${selectedItem.name}`}
      </CustomDialogTitle>
      <CustomDialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div style={{ height: 160, width: '100%' }}>
              <UploadCompAnt
                error={errors.url}
                isAddNew={true}
                label="Upload banner"
                fileData={formValue.url}
                onChange={handleChangeForm('url')}
                editable={true}
              />
            </div>
          </Grid>
          <Grid item xs={12}>
            <CustomInputLabel>Order</CustomInputLabel>
            <CustomTextField
              fullWidth
              variant="outlined"
              placeholder="Enter order"
              error={!!errors.order}
              helperText={errors.order}
              value={formValue.order || ''}
              onChange={handleChangeForm('order')}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInputLabel>URL</CustomInputLabel>
            <CustomTextField
              type="url"
              placeholder="Enter link"
              value={formValue.name}
              variant="outlined"
              fullWidth
              onChange={handleChangeForm('name')}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomInputLabel>Start date</CustomInputLabel>
            <CustomDate
              selectedDate={
                formValue.startDate ? moment(formValue.startDate) : ''
              }
              minDate={moment()}
              maxDate={formValue.endDate}
              isFullWidth
              placeholder="Select date"
              clearable={false}
              onChange={handleChangeForm('startDate')}
              style={{ height: 48 }}
            />
            {errors.startDate && (
              <CustomFormHelperText error>
                {errors.startDate}
              </CustomFormHelperText>
            )}
          </Grid>
          <Grid item xs={6}>
            <CustomInputLabel>End date</CustomInputLabel>
            <CustomDate
              selectedDate={formValue.endDate ? moment(formValue.endDate) : ''}
              minDate={formValue.startDate}
              isFullWidth
              placeholder="Select date"
              clearable={false}
              onChange={handleChangeForm('endDate')}
              style={{ height: 48 }}
            />
            {errors.endDate && (
              <CustomFormHelperText error>
                {errors.endDate}
              </CustomFormHelperText>
            )}
          </Grid>
        </Grid>
      </CustomDialogContent>

      <CustomDialogActions>
        <CustomLoadingButton
          type="submit"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
          loading={loading}
        >
          {typeModal === TYPE_MODAL.Create ? 'Create' : 'Save'}
        </CustomLoadingButton>
      </CustomDialogActions>
    </CustomDialog>
  );
};

ModalAddOrEditBanner.propTypes = {
  typeModal: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  selectedItem: PropTypes.object,
};

ModalAddOrEditBanner.defaultProps = {
  typeModal: TYPE_MODAL.Create,
  open: false,
  onClose: () => console.warn('Not implemented yet'),
  onSubmit: () => console.warn('Not implemented yet'),
  selectedItem: null,
};

export default ModalAddOrEditBanner;
